// Éditeur de catalogue (v2) — création/édition de rôles + éditeurs
// structurés des 11 templates. Pas de proto pour cet écran (rec10 était
// lecture seule): conçu dans le langage de l'app — des listes de lignes
// à ajouter/réordonner, jamais de JSON brut. Les recrutements en cours
// gardent leurs snapshots; seuls les nouveaux héritent des changements.
import { useState } from 'react';
import { ENTITIES, REC_DIMS, REC_OB_HORIZONS, REC_OB_OWNERS, type RecDim, type RecGridCrit } from '@pointage/shared';
import { toast } from '../../store/toast.js';
import {
  useRecRoleCreate, useRecRolePatch, useRecRoleTemplate, useRecRoleTemplateClear,
  type RecBands, type RecRoleDTO,
} from '../../api/hooks.js';
import { RecEnt, TPL_META } from './common.js';
import { useUnsavedGuard } from '../../lib/unsaved.js';

// Ids de lignes: générés une fois, stables ensuite — ils servent de clés
// dans les snapshots des candidatures (presel, scorecards).
const genId = () => `r${Math.random().toString(36).slice(2, 8)}`;

const arrMove = <T,>(a: T[], i: number, d: number): T[] => {
  const j = i + d;
  if (j < 0 || j >= a.length) return a;
  const b = [...a];
  [b[i], b[j]] = [b[j]!, b[i]!];
  return b;
};
const arrDel = <T,>(a: T[], i: number): T[] => a.filter((_, x) => x !== i);

const mutErr = (fallback: string) => (err: unknown) =>
  toast.danger((err as { body?: { message?: string } }).body?.message ?? fallback);

// ── petites briques ─────────────────────────────────────────

function RowCtl({ i, n, onMove, onDel }: { i: number; n: number; onMove: (d: number) => void; onDel: () => void }) {
  return (
    <span className="cated-ctl">
      <button type="button" className="cated-ib" disabled={i === 0} onClick={() => onMove(-1)} title="Monter">↑</button>
      <button type="button" className="cated-ib" disabled={i === n - 1} onClick={() => onMove(1)} title="Descendre">↓</button>
      <button type="button" className="cated-ib del" onClick={onDel} title="Supprimer la ligne">✕</button>
    </span>
  );
}

// Liste de lignes texte (missions, profil, questions de kit…).
function StrList({ label, rows, onChange, ph, addLabel }: {
  label?: string; rows: string[]; onChange: (rows: string[]) => void; ph: string; addLabel?: string;
}) {
  return (
    <div className="cated-sec">
      {label && <div className="rec-lbl">{label}</div>}
      {rows.map((v, i) => (
        <div key={i} className="cated-row">
          <input value={v} placeholder={ph} onChange={(e) => onChange(rows.map((x, j) => (j === i ? e.target.value : x)))} />
          <RowCtl i={i} n={rows.length} onMove={(d) => onChange(arrMove(rows, i, d))} onDel={() => onChange(arrDel(rows, i))} />
        </div>
      ))}
      <button type="button" className="cated-add" onClick={() => onChange([...rows, ''])}>＋ {addLabel ?? 'Ajouter une ligne'}</button>
    </div>
  );
}

// Coquille commune des éditeurs de template: entête, corps, pied
// Annuler / Vider / Enregistrer.
function EditorShell({ tplKey, hasValue, busy, onCancel, onClear, onSave, children }: {
  tplKey: string; hasValue: boolean; busy: boolean;
  onCancel: () => void; onClear: () => void; onSave: () => void;
  children: React.ReactNode;
}) {
  const meta = TPL_META[tplKey] ?? [tplKey, '📄', ''];
  const [confirmClear, setConfirmClear] = useState(false);
  // Un éditeur ouvert est une rédaction en cours : la popup de sortie
  // propose d'enregistrer le template ou de revenir à la fiche.
  useUnsavedGuard({
    id: `rec-template-${tplKey}`,
    dirty: !busy,
    label: `Recrutement · ${meta[0]}`,
    save: async () => onSave(),
    discard: onCancel,
  });
  return (
    <div className="cated">
      <div className="cated-head">
        <span className="ic">{meta[1]}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <b>{meta[0]}</b>
          <span>{meta[2]}</span>
        </div>
        <span className="cated-inherit">hérité par chaque nouveau recrutement — les dossiers en cours gardent leur copie</span>
      </div>
      {children}
      <div className="cated-foot">
        <button className="rec-ghost sm" disabled={busy} onClick={onCancel}>← Retour à la fiche</button>
        <span style={{ flex: 1 }}></span>
        {hasValue && !confirmClear && (
          <button className="rec-ghost sm danger" disabled={busy} onClick={() => setConfirmClear(true)}>Vider le template</button>
        )}
        {hasValue && confirmClear && (
          <button className="rec-ghost sm danger" disabled={busy} onClick={onClear}>Confirmer — repasser « À rédiger »</button>
        )}
        <button className="rec-go sm" disabled={busy} onClick={onSave}>Enregistrer le template</button>
      </div>
    </div>
  );
}

// Sauvegarde partagée: PATCH /roles/:id/template/:key + DELETE (vider).
function useTplSave(role: RecRoleDTO, tplKey: string, onDone: () => void) {
  const save = useRecRoleTemplate();
  const clear = useRecRoleTemplateClear();
  const meta = TPL_META[tplKey] ?? [tplKey];
  return {
    busy: save.isPending || clear.isPending,
    submit: (value: unknown) => save.mutate({ id: role.id, key: tplKey, value }, {
      onSuccess: () => { onDone(); toast.success(`« ${meta[0]} » enregistré — hérité par les prochains recrutements ${role.label}.`); },
      onError: mutErr('Enregistrement impossible.'),
    }),
    clear: () => clear.mutate({ id: role.id, key: tplKey }, {
      onSuccess: () => { onDone(); toast.success(`« ${meta[0]} » vidé — le template repasse « À rédiger ».`); },
      onError: mutErr('Impossible de vider ce template.'),
    }),
  };
}

const clean = (rows: string[]) => rows.map((r) => r.trim()).filter(Boolean);

// ── éditeurs par forme ──────────────────────────────────────

function FichePosteEd({ role, onDone }: { role: RecRoleDTO; onDone: () => void }) {
  const cur = (role.templates.fichePoste ?? {}) as { missions?: string[]; profil?: string[] };
  const [missions, setMissions] = useState<string[]>(cur.missions ?? ['']);
  const [profil, setProfil] = useState<string[]>(cur.profil ?? ['']);
  const { busy, submit, clear } = useTplSave(role, 'fichePoste', onDone);
  const save = () => {
    const m = clean(missions); const p = clean(profil);
    if (!m.length || !p.length) return toast.danger('Au moins une mission et une ligne de profil.');
    submit({ missions: m, profil: p });
  };
  return (
    <EditorShell tplKey="fichePoste" hasValue={!!role.templates.fichePoste} busy={busy} onCancel={onDone} onClear={clear} onSave={save}>
      <StrList label="Missions" rows={missions} onChange={setMissions} ph="Production motion des campagnes clients…" addLabel="Ajouter une mission" />
      <StrList label="Profil recherché" rows={profil} onChange={setProfil} ph="2+ ans en motion design…" addLabel="Ajouter un critère de profil" />
    </EditorShell>
  );
}

function AnnonceEd({ role, onDone }: { role: RecRoleDTO; onDone: () => void }) {
  const cur = (role.templates.annonce ?? {}) as { accroche?: string; missions?: string[]; profil?: string[]; offre?: string[]; process?: string[] };
  const [accroche, setAccroche] = useState(cur.accroche ?? '');
  const [missions, setMissions] = useState<string[]>(cur.missions ?? ['']);
  const [profil, setProfil] = useState<string[]>(cur.profil ?? ['']);
  const [offre, setOffre] = useState<string[]>(cur.offre ?? ['']);
  const [process, setProcess] = useState<string[]>(cur.process ?? ['Appel de qualification (30 min)', 'Entretien', 'Entretien final']);
  const { busy, submit, clear } = useTplSave(role, 'annonce', onDone);
  const save = () => {
    if (!accroche.trim()) return toast.danger('L’accroche est le cœur de l’annonce — rédigez-la.');
    submit({ accroche: accroche.trim(), missions: clean(missions), profil: clean(profil), offre: clean(offre), process: clean(process) });
  };
  return (
    <EditorShell tplKey="annonce" hasValue={!!role.templates.annonce} busy={busy} onCancel={onDone} onClear={clear} onSave={save}>
      <div className="cated-sec">
        <div className="rec-lbl">Accroche</div>
        <textarea className="cated-ta" rows={2} value={accroche} placeholder="Construire des produits qui servent des milliers d’utilisateurs…" onChange={(e) => setAccroche(e.target.value)} />
      </div>
      <StrList label="Missions" rows={missions} onChange={setMissions} ph="Feature ownership de bout en bout…" addLabel="Ajouter une mission" />
      <StrList label="Profil" rows={profil} onChange={setProfil} ph="3+ ans…" addLabel="Ajouter un critère" />
      <StrList label="Ce qu’on offre" rows={offre} onChange={setOffre} ph="Télétravail hybride…" addLabel="Ajouter un avantage" />
      <StrList label="Déroulé du process" rows={process} onChange={setProcess} ph="Appel de qualification (30 min)…" addLabel="Ajouter une étape" />
    </EditorShell>
  );
}

function PreselEd({ role, onDone }: { role: RecRoleDTO; onDone: () => void }) {
  const [rows, setRows] = useState<Array<{ id: string; lbl: string; elim: boolean }>>(
    ((role.templates.presel ?? [{ id: genId(), lbl: '' }]) as Array<{ id: string; lbl: string; elim?: boolean }>).map((r) => ({ ...r, elim: r.elim !== false })),
  );
  const { busy, submit, clear } = useTplSave(role, 'presel', onDone);
  const save = () => {
    const v = rows.map((r) => ({ ...r, lbl: r.lbl.trim() })).filter((r) => r.lbl);
    if (!v.length) return toast.danger('Ajoutez au moins un critère de présélection.');
    submit(v);
  };
  return (
    <EditorShell tplKey="presel" hasValue={!!role.templates.presel} busy={busy} onCancel={onDone} onClear={clear} onSave={save}>
      <p className="cat-note">Checklist PASS / FAIL appliquée à chaque CV. Un FAIL sur un critère <b>éliminatoire</b> oriente vers le rejet motivé ; un critère <b>indicatif</b> en FAIL est noté sans bloquer.</p>
      <div className="cated-sec">
        {rows.map((r, i) => (
          <div key={r.id} className="cated-row">
            <button type="button" className="cated-pf" style={{ cursor: 'pointer', border: 0, fontFamily: 'inherit', ...(r.elim ? { background: 'var(--ink)', color: 'var(--card)' } : {}) }}
              title="Cliquer pour basculer éliminatoire / indicatif"
              onClick={() => setRows(rows.map((x, j) => (j === i ? { ...x, elim: !x.elim } : x)))}>{r.elim ? 'ÉLIMINATOIRE' : 'INDICATIF'}</button>
            <input value={r.lbl} placeholder="≥ 2 ans d’expérience en motion…" onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, lbl: e.target.value } : x)))} />
            <RowCtl i={i} n={rows.length} onMove={(d) => setRows(arrMove(rows, i, d))} onDel={() => setRows(arrDel(rows, i))} />
          </div>
        ))}
        <button type="button" className="cated-add" onClick={() => setRows([...rows, { id: genId(), lbl: '', elim: true }])}>＋ Ajouter un critère</button>
      </div>
    </EditorShell>
  );
}

// v2 — script en étapes (SHIFTIN v1.0) : titre, texte à dire, question,
// relances. Préavis, trajet et prétentions sont des champs structurés de
// l'appel, reconnus par l'id de l'étape (preavis, lieu, pret).
function ScriptEd({ role, onDone }: { role: RecRoleDTO; onDone: () => void }) {
  type Step = { id: string; t: string; say?: string; q?: string; follow?: string[] };
  const init = ((role.templates.script ?? []) as unknown as Array<Partial<Step> & { id: string }>).filter((x) => x.t) as Step[];
  const [rows, setRows] = useState<Step[]>(init.length ? init : [{ id: genId(), t: '', q: '', follow: [] }]);
  const { busy, submit, clear } = useTplSave(role, 'script', onDone);
  const save = () => {
    const v = rows
      .map((r) => ({ id: r.id, t: r.t.trim(), ...(r.say?.trim() ? { say: r.say.trim() } : {}), ...(r.q?.trim() ? { q: r.q.trim() } : {}), ...(clean(r.follow ?? []).length ? { follow: clean(r.follow ?? []) } : {}) }))
      .filter((r) => r.t);
    if (!v.length) return toast.danger('Ajoutez au moins une étape au script.');
    submit(v);
  };
  const set = (i: number, patch: Partial<Step>) => setRows(rows.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <EditorShell tplKey="script" hasValue={!!role.templates.script} busy={busy} onCancel={onDone} onClear={clear} onSave={save}>
      <p className="cat-note">Script SHIFTIN v1.0 : une étape = un titre, un texte à dire ou une question, et des relances. Les étapes <code>preavis</code>, <code>lieu</code> et <code>pret</code> ouvrent les champs structurés (date, trajet, prétentions) ; la dernière étape porte les 4 contrôles de clôture.</p>
      {rows.map((r, i) => (
        <div key={r.id} className="cated-kit">
          <div className="cated-row">
            <span className="rec-vchip">{i}</span>
            <input className="cated-kitname" value={r.t} placeholder="Titre de l’étape (Poste actuel au quotidien…)" onChange={(e) => set(i, { t: e.target.value })} />
            <RowCtl i={i} n={rows.length} onMove={(d) => setRows(arrMove(rows, i, d))} onDel={() => setRows(arrDel(rows, i))} />
          </div>
          <div className="cated-row"><input value={r.say ?? ''} placeholder="Texte à dire (facultatif) — {{prenom}}, {{moi}}, {{entite}}" onChange={(e) => set(i, { say: e.target.value })} /></div>
          <div className="cated-row"><input value={r.q ?? ''} placeholder="Question (facultative)" onChange={(e) => set(i, { q: e.target.value })} /></div>
          <StrList rows={r.follow ?? []} onChange={(follow) => set(i, { follow })} ph="Relance propre au rôle…" addLabel="Ajouter une relance" />
        </div>
      ))}
      <button type="button" className="cated-add" onClick={() => setRows([...rows, { id: genId(), t: '', follow: [] }])}>＋ Ajouter une étape</button>
    </EditorShell>
  );
}

function KitsEd({ role, onDone }: { role: RecRoleDTO; onDone: () => void }) {
  const cur = (role.templates.kits ?? {}) as Record<string, string[]>;
  const [kits, setKits] = useState<Array<{ name: string; qs: string[] }>>(
    Object.keys(cur).length
      ? Object.entries(cur).map(([name, qs]) => ({ name, qs }))
      : [{ name: 'Entretien manager', qs: [''] }],
  );
  const { busy, submit, clear } = useTplSave(role, 'kits', onDone);
  const save = () => {
    const entries: Array<[string, string[]]> = [];
    for (const k of kits) {
      const name = k.name.trim();
      const qs = clean(k.qs);
      if (name && qs.length) entries.push([name, qs]);
    }
    if (!entries.length) return toast.danger('Ajoutez au moins un kit avec une question.');
    if (new Set(entries.map(([n]) => n)).size !== entries.length) return toast.danger('Deux kits portent le même nom.');
    submit(Object.fromEntries(entries));
  };
  const set = (i: number, patch: Partial<{ name: string; qs: string[] }>) =>
    setKits(kits.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <EditorShell tplKey="kits" hasValue={!!role.templates.kits} busy={busy} onCancel={onDone} onClear={clear} onSave={save}>
      <p className="cat-note">Un kit par type d’entretien — son déroulé est snapshoté à la planification et guide le jury.</p>
      {kits.map((k, i) => (
        <div key={i} className="cated-kit">
          <div className="cated-row">
            <input className="cated-kitname" value={k.name} placeholder="Entretien technique…" onChange={(e) => set(i, { name: e.target.value })} />
            <button type="button" className="cated-ib del" title="Supprimer ce kit" onClick={() => setKits(arrDel(kits, i))}>✕</button>
          </div>
          <StrList rows={k.qs} onChange={(qs) => set(i, { qs })} ph="Revue de portfolio commentée…" addLabel="Ajouter une question" />
        </div>
      ))}
      <button type="button" className="cated-add" onClick={() => setKits([...kits, { name: '', qs: [''] }])}>＋ Ajouter un kit d’entretien</button>
    </EditorShell>
  );
}

function DimPick({ v, onSet }: { v: RecDim | undefined; onSet: (d: RecDim) => void }) {
  return (
    <span className="rv2-dimsel">
      {REC_DIMS.map((d) => <button key={d.id} type="button" className={v === d.id ? 'on' : ''} style={{ '--dc': d.c } as React.CSSProperties} onClick={() => onSet(d.id)}>{d.short}</button>)}
    </span>
  );
}
function CritRows({ rows, setRows, ph }: { rows: RecGridCrit[]; setRows: (r: RecGridCrit[]) => void; ph: string }) {
  const set = (i: number, patch: Partial<RecGridCrit>) => setRows(rows.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="cated-sec">
      {rows.map((r, i) => (
        <div key={r.id} className="cated-row" style={{ flexWrap: 'wrap' }}>
          <input value={r.lbl} placeholder={ph} onChange={(e) => set(i, { lbl: e.target.value })} />
          <select className="cated-co" value={r.co} onChange={(e) => set(i, { co: Number(e.target.value) })}>
            {[1, 2, 3, 4, 5].map((c) => <option key={c} value={c}>×{c}</option>)}
          </select>
          <DimPick v={r.dim} onSet={(dim) => set(i, { dim })} />
          <RowCtl i={i} n={rows.length} onMove={(d) => setRows(arrMove(rows, i, d))} onDel={() => setRows(arrDel(rows, i))} />
        </div>
      ))}
      <button type="button" className="cated-add" onClick={() => setRows([...rows, { id: genId(), lbl: '', co: 2, dim: 'CONNAISSANCES' }])}>＋ Ajouter un critère</button>
    </div>
  );
}

// v2 — chaque critère porte une dimension ; la grille n'est « prête » que
// si les 4 dimensions sont couvertes.
function GridEd({ role, onDone }: { role: RecRoleDTO; onDone: () => void }) {
  const [rows, setRows] = useState<RecGridCrit[]>(
    (role.templates.grid ?? [{ id: genId(), lbl: '', co: 2, dim: 'PERFORMANCE' }]) as RecGridCrit[],
  );
  const { busy, submit, clear } = useTplSave(role, 'grid', onDone);
  const cov = REC_DIMS.map((d) => ({ d, n: rows.filter((r) => r.dim === d.id && r.lbl.trim()).length }));
  const save = () => {
    const v = rows.map((r) => ({ ...r, lbl: r.lbl.trim() })).filter((r) => r.lbl);
    if (!v.length) return toast.danger('Ajoutez au moins un critère pondéré.');
    if (v.some((r) => !r.dim)) return toast.danger('Chaque critère doit porter une dimension.');
    submit(v);
  };
  return (
    <EditorShell tplKey="grid" hasValue={!!role.templates.grid} busy={busy} onCancel={onDone} onClear={clear} onSave={save}>
      <p className="cat-note">Chaque scorecard note ces critères /5, par dimension (Performance, Connaissances, Personnalité, Alignement). La barre se calcule sur la moyenne globale et par dimension. Modifier la grille ne réécrit pas les scorecards déjà soumises.</p>
      <div className="rv2-cov">
        {cov.map(({ d, n }) => (
          <div key={d.id} className={`rv2-covc${n ? '' : ' miss'}`} style={{ '--dc': d.c } as React.CSSProperties}>
            <b><i></i>{d.lbl}</b><div className="n">{n}</div><span>{n ? `critère${n > 1 ? 's' : ''}` : 'aucun critère — grille incomplète'}</span>
          </div>
        ))}
      </div>
      <CritRows rows={rows} setRows={setRows} ph="A livré un résultat malgré un vrai obstacle…" />
    </EditorShell>
  );
}

// v2 — 12ᵉ template : cas pratique rémunéré.
function TestEd({ role, onDone }: { role: RecRoleDTO; onDone: () => void }) {
  const cur = (role.templates.test ?? {}) as { brief?: string; deliverables?: string[]; delayH?: number; rubric?: RecGridCrit[] };
  const [brief, setBrief] = useState(cur.brief ?? '');
  const [deliv, setDeliv] = useState<string[]>(cur.deliverables ?? ['']);
  const [delayH, setDelayH] = useState<48 | 72>(cur.delayH === 48 ? 48 : 72);
  const [rubric, setRubric] = useState<RecGridCrit[]>(cur.rubric ?? [{ id: genId(), lbl: '', co: 2, dim: 'PERFORMANCE' }]);
  const { busy, submit, clear } = useTplSave(role, 'test', onDone);
  const save = () => {
    const rb = rubric.map((r) => ({ ...r, lbl: r.lbl.trim() })).filter((r) => r.lbl);
    if (brief.trim().length < 10) return toast.danger('Rédigez le brief (10 caractères minimum).');
    if (!clean(deliv).length) return toast.danger('Ajoutez au moins un livrable.');
    if (!rb.length) return toast.danger('Ajoutez au moins un critère de rubrique.');
    submit({ brief: brief.trim(), deliverables: clean(deliv), delayH, rubric: rb });
  };
  return (
    <EditorShell tplKey="test" hasValue={!!role.templates.test} busy={busy} onCancel={onDone} onClear={clear} onSave={save}>
      <p className="cat-note">Copié dans chaque candidature à l’ouverture du cas pratique (après l’appel). L’indemnité par défaut se règle dans Paramètres › Modules › Recrutement. Sous la note minimale, le candidat n’avance pas.</p>
      <div className="cated-sec">
        <div className="rec-lbl">Brief</div>
        <textarea className="cated-ta" rows={4} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Ce que le candidat doit produire, avec quels fichiers…"></textarea>
      </div>
      <div className="cated-sec">
        <div className="rec-lbl">Livrables</div>
        <StrList rows={deliv} onChange={setDeliv} ph="MP4 1080×1350, 15 s…" addLabel="Ajouter un livrable" />
      </div>
      <div className="cated-sec">
        <div className="rec-lbl">Délai</div>
        <div className="rec-seg">{([48, 72] as const).map((h) => <button key={h} type="button" className={delayH === h ? 'on' : ''} onClick={() => setDelayH(h)}>{h} h</button>)}</div>
      </div>
      <div className="rec-lbl">Rubrique de notation — même format que la grille</div>
      <CritRows rows={rubric} setRows={setRubric} ph="Le rendu répond au brief…" />
    </EditorShell>
  );
}

const OFFER_VARS = ['poste', 'entite', 'start', 'salaire', 'primes', 'essai', 'validite'];

function OffreEd({ role, onDone }: { role: RecRoleDTO; onDone: () => void }) {
  const cur = (role.templates.offre ?? {}) as { corps?: string };
  const [corps, setCorps] = useState(cur.corps ?? '');
  const { busy, submit, clear } = useTplSave(role, 'offre', onDone);
  const save = () => {
    if (!corps.trim()) return toast.danger('Le corps du modèle d’offre est vide.');
    submit({ corps: corps.trim() });
  };
  return (
    <EditorShell tplKey="offre" hasValue={!!role.templates.offre} busy={busy} onCancel={onDone} onClear={clear} onSave={save}>
      <p className="cat-note">Corps de la lettre d’offre (PDF joint à l’email). Les variables se remplissent depuis l’offre construite — cliquez pour insérer.</p>
      <div className="cated-vars">
        {OFFER_VARS.map((v) => (
          <button key={v} type="button" className="cated-varchip" onClick={() => setCorps((c) => `${c}${c && !c.endsWith(' ') ? ' ' : ''}{{${v}}}`)}>{`{{${v}}}`}</button>
        ))}
      </div>
      <textarea
        className="cated-ta" rows={8} value={corps}
        placeholder="Nous avons le plaisir de vous proposer le poste de {{poste}} au sein de {{entite}}, à compter du {{start}}…"
        onChange={(e) => setCorps(e.target.value)}
      />
    </EditorShell>
  );
}

function DossierEd({ role, onDone }: { role: RecRoleDTO; onDone: () => void }) {
  const [rows, setRows] = useState<Array<{ id: string; nm: string }>>(
    (role.templates.dossier ?? [{ id: genId(), nm: '' }]) as Array<{ id: string; nm: string }>,
  );
  const { busy, submit, clear } = useTplSave(role, 'dossier', onDone);
  const save = () => {
    const v = rows.map((r) => ({ ...r, nm: r.nm.trim() })).filter((r) => r.nm);
    if (!v.length) return toast.danger('Ajoutez au moins une pièce au dossier.');
    submit(v);
  };
  return (
    <EditorShell tplKey="dossier" hasValue={!!role.templates.dossier} busy={busy} onCancel={onDone} onClear={clear} onSave={save}>
      <p className="cat-note">Pièces demandées à la recrue au moment de l’onboarding — suivies manquante → reçue → validée.</p>
      <div className="cated-sec">
        {rows.map((r, i) => (
          <div key={r.id} className="cated-row">
            <span className="cated-pf">🗂</span>
            <input value={r.nm} placeholder="Extrait de naissance…" onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, nm: e.target.value } : x)))} />
            <RowCtl i={i} n={rows.length} onMove={(d) => setRows(arrMove(rows, i, d))} onDel={() => setRows(arrDel(rows, i))} />
          </div>
        ))}
        <button type="button" className="cated-add" onClick={() => setRows([...rows, { id: genId(), nm: '' }])}>＋ Ajouter une pièce</button>
      </div>
    </EditorShell>
  );
}

type ObPlan = Record<'J1' | 'S1' | 'M1' | 'J90', Array<{ tx: string; ow: string }>>;

function OnboardingEd({ role, onDone }: { role: RecRoleDTO; onDone: () => void }) {
  const cur = (role.templates.onboarding ?? {}) as Partial<ObPlan>;
  const [plan, setPlan] = useState<ObPlan>({
    J1: cur.J1 ?? [], S1: cur.S1 ?? [], M1: cur.M1 ?? [], J90: cur.J90 ?? [],
  });
  const { busy, submit, clear } = useTplSave(role, 'onboarding', onDone);
  const owners = Object.keys(REC_OB_OWNERS);
  const save = () => {
    const v = Object.fromEntries(
      (Object.keys(plan) as Array<keyof ObPlan>).map((h) => [h, plan[h].map((t) => ({ tx: t.tx.trim(), ow: t.ow })).filter((t) => t.tx)]),
    ) as unknown as ObPlan;
    if (!v.J1.length && !v.S1.length && !v.M1.length && !v.J90.length) return toast.danger('Le plan est vide — ajoutez au moins une tâche.');
    submit(v);
  };
  const setH = (h: keyof ObPlan, rows: Array<{ tx: string; ow: string }>) => setPlan({ ...plan, [h]: rows });
  return (
    <EditorShell tplKey="onboarding" hasValue={!!role.templates.onboarding} busy={busy} onCancel={onDone} onClear={clear} onSave={save}>
      <p className="cat-note">Plan d’intégration J1 → J90, copié pour chaque recrue à la signature — chaque tâche a un responsable.</p>
      {REC_OB_HORIZONS.map((hz) => {
        const rows = plan[hz.id as keyof ObPlan];
        return (
          <div key={hz.id} className="cated-sec">
            <div className="rec-lbl" style={{ color: hz.c }}>{hz.lbl} — avant J+{hz.due}</div>
            {rows.map((r, i) => (
              <div key={i} className="cated-row">
                <input value={r.tx} placeholder="Accueil, tour des locaux & équipe…" onChange={(e) => setH(hz.id as keyof ObPlan, rows.map((x, j) => (j === i ? { ...x, tx: e.target.value } : x)))} />
                <select className="cated-co" value={r.ow} onChange={(e) => setH(hz.id as keyof ObPlan, rows.map((x, j) => (j === i ? { ...x, ow: e.target.value } : x)))}>
                  {owners.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <RowCtl i={i} n={rows.length} onMove={(d) => setH(hz.id as keyof ObPlan, arrMove(rows, i, d))} onDel={() => setH(hz.id as keyof ObPlan, arrDel(rows, i))} />
              </div>
            ))}
            <button type="button" className="cated-add" onClick={() => setH(hz.id as keyof ObPlan, [...rows, { tx: '', ow: 'Manager' }])}>＋ Tâche {hz.lbl}</button>
          </div>
        );
      })}
    </EditorShell>
  );
}

function OneOnOneEd({ role, onDone }: { role: RecRoleDTO; onDone: () => void }) {
  const [rows, setRows] = useState<Array<{ when: string; agenda: string }>>(
    (role.templates.oneOnOne ?? [{ when: 'J+7', agenda: '' }]) as Array<{ when: string; agenda: string }>,
  );
  const { busy, submit, clear } = useTplSave(role, 'oneOnOne', onDone);
  const save = () => {
    const v = rows.map((r) => ({ when: r.when.trim(), agenda: r.agenda.trim() })).filter((r) => r.when && r.agenda);
    if (!v.length) return toast.danger('Ajoutez au moins un 1:1 planifié.');
    submit(v);
  };
  const set = (i: number, patch: Partial<{ when: string; agenda: string }>) =>
    setRows(rows.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <EditorShell tplKey="oneOnOne" hasValue={!!role.templates.oneOnOne} busy={busy} onCancel={onDone} onClear={clear} onSave={save}>
      <p className="cat-note">Rythme de 1:1 manager ↔ recrue pendant la période d’essai.</p>
      <div className="cated-sec">
        {rows.map((r, i) => (
          <div key={i} className="cated-row">
            <input className="cated-when" value={r.when} placeholder="J+7" onChange={(e) => set(i, { when: e.target.value })} />
            <input value={r.agenda} placeholder="Premières impressions, blocages d’installation…" onChange={(e) => set(i, { agenda: e.target.value })} />
            <RowCtl i={i} n={rows.length} onMove={(d) => setRows(arrMove(rows, i, d))} onDel={() => setRows(arrDel(rows, i))} />
          </div>
        ))}
        <button type="button" className="cated-add" onClick={() => setRows([...rows, { when: '', agenda: '' }])}>＋ Ajouter un 1:1</button>
      </div>
    </EditorShell>
  );
}

function RessourcesEd({ role, onDone }: { role: RecRoleDTO; onDone: () => void }) {
  const [rows, setRows] = useState<Array<{ nm: string; when: string; ic: string }>>(
    (role.templates.ressources ?? [{ nm: '', when: 'Avant J1', ic: '📄' }]) as Array<{ nm: string; when: string; ic: string }>,
  );
  const { busy, submit, clear } = useTplSave(role, 'ressources', onDone);
  const save = () => {
    const v = rows.map((r) => ({ nm: r.nm.trim(), when: r.when.trim(), ic: r.ic.trim() || '📄' })).filter((r) => r.nm && r.when);
    if (!v.length) return toast.danger('Ajoutez au moins une ressource.');
    submit(v);
  };
  const set = (i: number, patch: Partial<{ nm: string; when: string; ic: string }>) =>
    setRows(rows.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <EditorShell tplKey="ressources" hasValue={!!role.templates.ressources} busy={busy} onCancel={onDone} onClear={clear} onSave={save}>
      <p className="cat-note">Ressources partagées à la recrue (handbook, accès outils…) — chacune avec son moment.</p>
      <div className="cated-sec">
        {rows.map((r, i) => (
          <div key={i} className="cated-row">
            <input className="cated-ic" value={r.ic} maxLength={4} onChange={(e) => set(i, { ic: e.target.value })} title="Emoji" />
            <input value={r.nm} placeholder="Employee handbook…" onChange={(e) => set(i, { nm: e.target.value })} />
            <input className="cated-when" value={r.when} placeholder="Avant J1 / J1 / Semaine 1…" onChange={(e) => set(i, { when: e.target.value })} />
            <RowCtl i={i} n={rows.length} onMove={(d) => setRows(arrMove(rows, i, d))} onDel={() => setRows(arrDel(rows, i))} />
          </div>
        ))}
        <button type="button" className="cated-add" onClick={() => setRows([...rows, { nm: '', when: 'J1', ic: '📄' }])}>＋ Ajouter une ressource</button>
      </div>
    </EditorShell>
  );
}

const EDITORS: Record<string, (p: { role: RecRoleDTO; onDone: () => void }) => React.JSX.Element> = {
  fichePoste: FichePosteEd, annonce: AnnonceEd, presel: PreselEd, script: ScriptEd,
  kits: KitsEd, grid: GridEd, test: TestEd, offre: OffreEd, dossier: DossierEd,
  onboarding: OnboardingEd, oneOnOne: OneOnOneEd, ressources: RessourcesEd,
};

/** Éditeur plein panneau d'un template — remonté par clé pour réinitialiser le brouillon. */
export function TplEditor({ role, tplKey, onDone }: { role: RecRoleDTO; tplKey: string; onDone: () => void }) {
  const Ed = EDITORS[tplKey];
  if (!Ed) return null;
  return <Ed key={`${role.id}:${tplKey}`} role={role} onDone={onDone} />;
}

// ── fourchettes salariales par entité ───────────────────────
// Éditeur inline de la fiche rôle — visible seulement avec
// recrutement.salaires (le serveur l'exige aussi en écriture).
export function BandsEditor({ role }: { role: RecRoleDTO }) {
  const patch = useRecRolePatch();
  const init = () => Object.fromEntries(
    ENTITIES.map((e) => {
      const b = role.bands?.[e.id];
      return [e.id, { lo: b ? String(b[0]) : '', hi: b ? String(b[1]) : '' }];
    }),
  ) as Record<string, { lo: string; hi: string }>;
  const [draft, setDraft] = useState(init);
  const dirty = JSON.stringify(draft) !== JSON.stringify(init());

  // null = saisie invalide (le toast dit laquelle).
  const buildBands = (): RecBands | null => {
    const bands: RecBands = {};
    for (const e of ENTITIES) {
      const d = draft[e.id]!;
      const lo = d.lo.trim() === '' ? null : Number(d.lo);
      const hi = d.hi.trim() === '' ? null : Number(d.hi);
      if (lo == null && hi == null) continue;
      if (lo == null || hi == null || Number.isNaN(lo) || Number.isNaN(hi)) {
        toast.danger(`${e.name} : renseignez le bas ET le haut de fourchette (ou aucun).`);
        return null;
      }
      if (lo > hi) { toast.danger(`${e.name} : le bas de fourchette dépasse le haut.`); return null; }
      bands[e.id] = [lo, hi];
    }
    return bands;
  };
  const save = () => {
    const bands = buildBands();
    if (!bands) return;
    patch.mutate({ id: role.id, bands }, {
      onSuccess: () => toast.success(`Fourchettes ${role.label} enregistrées — hors fourchette, une offre est signalée à la validation.`),
      onError: mutErr('Enregistrement impossible.'),
    });
  };
  useUnsavedGuard({
    id: `rec-bands-${role.id}`,
    dirty,
    label: `Recrutement · fourchettes ${role.label}`,
    save: async () => {
      const bands = buildBands();
      if (!bands) throw new Error('invalid');
      await patch.mutateAsync({ id: role.id, bands });
      toast.success(`Fourchettes ${role.label} enregistrées.`);
    },
    discard: () => setDraft(init()),
  });

  return (
    <div>
      {ENTITIES.map((e) => {
        const d = draft[e.id]!;
        return (
          <div key={e.id} className="cated-bandrow">
            <RecEnt id={e.id} name={e.name} color={e.color} />
            <input type="number" min={0} step={5000} value={d.lo} placeholder="min" onChange={(ev) => setDraft({ ...draft, [e.id]: { ...d, lo: ev.target.value } })} />
            <span className="sep">→</span>
            <input type="number" min={0} step={5000} value={d.hi} placeholder="max" onChange={(ev) => setDraft({ ...draft, [e.id]: { ...d, hi: ev.target.value } })} />
            <span className="cur">DZD</span>
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <button className="rec-go sm" disabled={!dirty || patch.isPending} onClick={save}>Enregistrer les fourchettes</button>
        {dirty && <button className="rec-ghost sm" disabled={patch.isPending} onClick={() => setDraft(init())}>Annuler</button>}
      </div>
      <p className="cat-note" style={{ marginTop: 10 }}>Entité vide = fourchette à définir. Toute offre hors fourchette sera signalée à la chaîne de validation — visible RH & direction uniquement.</p>
    </div>
  );
}

// ── modal rôle (création / renommage / archivage) ───────────
export function RoleModal({ role, depts, onClose }: {
  role: RecRoleDTO | null; depts: string[]; onClose: () => void;
}) {
  const create = useRecRoleCreate();
  const patch = useRecRolePatch();
  const [label, setLabel] = useState(role?.label ?? '');
  const [dept, setDept] = useState(role?.dept ?? '');
  const busy = create.isPending || patch.isPending;

  const save = () => {
    if (label.trim().length < 2 || dept.trim().length < 2) {
      return toast.danger('Intitulé et département sont requis.');
    }
    if (role) {
      patch.mutate({ id: role.id, label: label.trim(), dept: dept.trim() }, {
        onSuccess: (r) => { onClose(); toast.success(`Rôle « ${r.label} » mis à jour.`); },
        onError: mutErr('Mise à jour impossible.'),
      });
    } else {
      create.mutate({ label: label.trim(), dept: dept.trim() }, {
        onSuccess: (r) => { onClose(); toast.success(`Rôle « ${r.label} » créé — rédigez ses 11 templates pour le rendre prêt à recruter.`); },
        onError: mutErr('Création impossible.'),
      });
    }
  };
  const setActive = (active: boolean) => {
    if (!role) return;
    patch.mutate({ id: role.id, active }, {
      onSuccess: () => { onClose(); toast.success(active ? `Rôle « ${role.label} » restauré au catalogue.` : `Rôle « ${role.label} » archivé — les demandes existantes ne bougent pas.`); },
      onError: mutErr('Opération impossible.'),
    });
  };

  return (
    <div className="rec-modal-ov" onClick={onClose}>
      <div className="rec-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rec-modal-h">
          <h3>{role ? `Modifier le rôle — ${role.label}` : 'Nouveau rôle au catalogue'}</h3>
          <button className="rec-x" onClick={onClose} aria-label="Fermer">✕</button>
        </div>
        <div className="rec-modal-b">
          {!role && <p className="cat-note">Un rôle = un modèle réutilisable: fiche de poste, grille, script, plans… Chaque recrutement ouvert depuis ce rôle en hérite.</p>}
          <div className="rec-field">
            <label>Intitulé du rôle</label>
            <input value={label} placeholder="Motion Designer…" onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div className="rec-field">
            <label>Département</label>
            <input value={dept} list="cated-depts" placeholder="Créa / Tech / Commercial…" onChange={(e) => setDept(e.target.value)} />
            <datalist id="cated-depts">
              {depts.map((d) => <option key={d} value={d} />)}
            </datalist>
          </div>
          <div className="rec-modal-f">
            {role && (role.active
              ? <button className="rec-ghost sm danger" disabled={busy} onClick={() => setActive(false)}>Archiver le rôle</button>
              : <button className="rec-ghost sm" disabled={busy} onClick={() => setActive(true)}>Restaurer au catalogue</button>
            )}
            <span style={{ flex: 1 }}></span>
            <button className="rec-ghost sm" disabled={busy} onClick={onClose}>Annuler</button>
            <button className="rec-go sm" disabled={busy} onClick={save}>{role ? 'Enregistrer' : 'Créer le rôle'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
